import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Volume2, Pause } from "lucide-react";
import { fetchTodayDevotional, fetchDevotional, fetchDevotionals } from "../lib/api";
import { trackDevotional } from "../lib/readingProgress";
import { markTodayCompleted } from "../lib/dailyJourney";
import { PastoralBlocks, pastoralBlocksHasContent, pastoralBlocksToText } from "../components/PastoralBlocks";
import { ScriptureConnections } from "../components/ScriptureConnections";
import useDaily from "../lib/useDaily";
import useTTS from "../lib/useTTS";
import JournalPrompt from "../components/JournalPrompt";
import NewsletterCTA from "../components/NewsletterCTA";
import PrayerWeekCard from "../components/PrayerWeekCard";
import ShareButton from "../features/share/ShareButton";
import { Skeleton } from "../components/ui/skeleton";
import { BibleRefLink } from "../lib/bibleRef";
import { LiturgicalBadge, JourneyDayLabel } from "../components/LiturgicalBadge";

const Section = ({ label, children }) => (
  <div className="mt-8">
    <div className="flex items-center gap-3">
      <span className="text-[10px] uppercase tracking-[0.3em] text-gold">{label}</span>
      <span className="flex-1 h-px bg-border" />
    </div>
    <div className="mt-4 text-navy/90 leading-relaxed text-base sm:text-[17px]">{children}</div>
  </div>
);

// Fase 1 (iter56): sintetiza um devocional a partir do payload /api/daily
// (mesma fonte de verdade da Home e Dashboard). Assim /devocional deixa de
// consumir o endpoint legado /api/devotional/today (que está congelado na
// landing) e passa a rotacionar diariamente em sincronia com o resto do app.
function dailyToDevotional(daily) {
  if (!daily || !daily.devotional) return null;
  const dv = daily.devotional;
  const title = dv.title || "";
  if (!title) return null;
  return {
    id: dv.id || daily.cache_key || "today",
    date: daily.date || "",
    title,
    verse_ref: dv.verse_ref || (daily.central_verse && daily.central_verse.reference) || "",
    verse_text: dv.verse_text || (daily.central_verse && daily.central_verse.text) || "",
    reflection: dv.reflection || "",
    application: dv.application || daily.phrase || "",
    prayer: dv.prayer || daily.prayer || "",
    impact_phrase: dv.impact_phrase || daily.phrase || "",
    pastoral_blocks: daily.pastoral_blocks || null,
    scripture_connections: daily.scripture_connections || [],
    published: true,
  };
}

export default function DevocionalDia() {
  const { id } = useParams();
  const { daily, loading: dailyLoading } = useDaily();
  const [devo, setDevo] = useState(null);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sintetiza a partir de /api/daily quando não há :id na URL (rota /devocional).
  const dailyDevo = useMemo(() => (id ? null : dailyToDevotional(daily)), [id, daily]);

  useEffect(() => {
    // Rota /devocional/:id (histórico) → sempre lookup específico.
    if (id) {
      setLoading(true);
      Promise.all([fetchDevotional(id), fetchDevotionals()])
        .then(([d, list]) => {
          setDevo(d);
          setAll(list);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
      return;
    }

    // Rota /devocional (hoje) → aguarda useDaily settar. Enquanto daily
    // está carregando NÃO caímos no legacy (evita race que sobrescreve o
    // devocional correto do /api/daily com o legado congelado).
    if (dailyLoading) {
      setLoading(true);
      return;
    }

    // Carrega lista de devocionais (histórico) em paralelo, sempre.
    fetchDevotionals().then(setAll).catch(() => {});

    if (dailyDevo) {
      setDevo(dailyDevo);
      setLoading(false);
    } else {
      // Fallback legado: só se /api/daily terminou de carregar e veio vazio.
      let cancelled = false;
      setLoading(true);
      fetchTodayDevotional()
        .then((leg) => {
          if (!cancelled) setDevo(leg);
        })
        .catch(() => {})
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }
  }, [id, dailyDevo, dailyLoading]);

  useEffect(() => {
    if (devo?.title) {
      trackDevotional({ id: devo.id || id || "today", title: devo.title });
      markTodayCompleted();
    }
  }, [devo, id]);

  const tts = useTTS();
  const onTTSClick = () => {
    if (!devo) return;
    if (tts.playing && !tts.paused) { tts.pause(); return; }
    if (tts.playing && tts.paused) { tts.resume(); return; }
    const items = [];
    if (devo.title) items.push({ id: "title", text: devo.title });
    if (devo.verse_text) items.push({ id: "verse", text: devo.verse_text });
    if (devo.pastoral_blocks && pastoralBlocksHasContent(devo.pastoral_blocks)) {
      items.push({ id: "blocks", text: pastoralBlocksToText(devo.pastoral_blocks) });
    } else {
      if (devo.reflection) items.push({ id: "reflection", text: devo.reflection });
      if (devo.application) items.push({ id: "application", text: devo.application });
      if (devo.prayer) items.push({ id: "prayer", text: devo.prayer });
    }
    tts.speak(items);
  };

  return (
    <div className="max-w-5xl mx-auto section-padding py-8" data-testid="devotional-page">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 hover:text-navy mb-6"
        data-testid="devotional-back"
      >
        <ArrowLeft className="w-4 h-4" /> Início
      </Link>

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-2/3" />
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : devo ? (
        <article className="reading-max mx-auto fade-in">
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-navy/50">
                <BookOpen className="w-3.5 h-3.5" /> Devocional
              </div>
              {!id && <JourneyDayLabel cycleDay={daily?.cycle_day} testid="devocional-journey-day" />}
            </div>
            {tts.supported && (
              <button
                type="button"
                onClick={onTTSClick}
                aria-label={tts.playing && !tts.paused ? "Pausar" : "Ouvir devocional"}
                data-testid="devotional-tts"
                data-state={tts.playing ? (tts.paused ? "paused" : "playing") : "idle"}
                className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${
                  tts.playing
                    ? "bg-gold border-gold text-navy"
                    : "bg-white border-border text-navy/60 hover:text-gold"
                }`}
              >
                {tts.playing && !tts.paused ? <Pause className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
            )}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-navy mt-3 leading-[1.08]">
            {devo.title}
          </h1>
          <BibleRefLink
            refText={devo.verse_ref}
            testid="devocional-verse-ref-link"
            className="mt-4 inline-block text-gold text-sm tracking-wide hover:underline hover:text-navy transition-colors"
          />
          {!id && (
            <div className="mt-3">
              <LiturgicalBadge weeklyTheme={daily?.weekly_theme} testid="devocional-liturgical-badge" />
            </div>
          )}

          {(devo.verse_text || "").trim() ? (
            <blockquote className="mt-6 border-l-2 border-gold/70 pl-5 py-1 font-display text-xl text-navy/90 leading-snug">
              “{devo.verse_text}”
            </blockquote>
          ) : null}

          {devo.pastoral_blocks && pastoralBlocksHasContent(devo.pastoral_blocks) ? (
            <div className="mt-8">
              <PastoralBlocks blocks={devo.pastoral_blocks} testidPrefix="devocional-block" />
              {Array.isArray(devo.scripture_connections) && devo.scripture_connections.length > 0 && (
                <div className="mt-8" data-testid="devocional-connections">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">Vá além, leia também</p>
                  <ScriptureConnections
                    connections={devo.scripture_connections}
                    testidPrefix="devocional-connection"
                  />
                </div>
              )}
            </div>
          ) : id ? (
            <>
              {/* Histórico local (:id), formato legado documentado */}
              {devo.reflection && (
                <Section label="Reflexão">
                  {devo.reflection.split("\n").map((p, i) => (
                    <p key={i} className="mb-4">
                      {p}
                    </p>
                  ))}
                </Section>
              )}
              {devo.application && (
                <Section label="Aplicação prática">
                  <p>{devo.application}</p>
                </Section>
              )}
              {devo.prayer && (
                <Section label="Oração final">
                  <p className="italic text-navy/85">{devo.prayer}</p>
                </Section>
              )}
            </>
          ) : (
            <div
              className="mt-8 rounded-2xl bg-[hsl(var(--parchment))] border border-border p-6"
              data-testid="devocional-preparando"
            >
              <p className="text-navy/80 text-[15px] leading-relaxed">
                O devocional de hoje está sendo preparado com oração e cuidado.
                Volte em instantes.
              </p>
            </div>
          )}

          {/* Fase 2 (iter58) — Campos do Calendário Pastoral (defensivos).
              Renderizam somente quando a landing publicar os campos. */}
          {(() => {
            const desafio = (daily?.desafio_do_dia || "").trim();
            if (!desafio) return null;
            return (
              <div className="mt-8" data-testid="devocional-desafio-section">
                <div className="flex items-center gap-3">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-gold">
                    Desafio do dia
                  </span>
                  <span className="flex-1 h-px bg-border" />
                </div>
                <div
                  className="mt-4 rounded-2xl bg-gold/8 border border-gold/30 p-5"
                  style={{ background: "rgba(201, 169, 97, 0.08)" }}
                  data-testid="devocional-desafio-text"
                >
                  <p className="text-navy/90 leading-relaxed text-[16px]">
                    {desafio}
                  </p>
                </div>
              </div>
            );
          })()}

          {(() => {
            const pergunta = (daily?.pergunta_reflexao || "").trim();
            if (!pergunta) return null;
            return (
              <div className="mt-8" data-testid="devocional-pergunta-section">
                <div className="flex items-center gap-3">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-gold">
                    Pergunta para reflexão
                  </span>
                  <span className="flex-1 h-px bg-border" />
                </div>
                <p
                  className="mt-4 font-display text-lg sm:text-xl text-navy/90 leading-snug italic"
                  data-testid="devocional-pergunta-text"
                >
                  {pergunta}
                </p>
              </div>
            );
          })()}

          {(() => {
            const vm = daily?.versiculo_memoria_hoje;
            const vRef = (vm?.reference || vm?.verse_reference || "").trim();
            const vText = (vm?.text || vm?.verse_text || (typeof vm === "string" ? vm : "")).trim();
            if (!vRef && !vText) return null;
            return (
              <div className="mt-8" data-testid="devocional-versiculo-memoria-section">
                <div className="flex items-center gap-3">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-gold">
                    Versículo para memorizar
                  </span>
                  <span className="flex-1 h-px bg-border" />
                </div>
                <div
                  className="mt-4 rounded-2xl bg-[hsl(var(--parchment))] border border-navy/10 p-5"
                  data-testid="devocional-versiculo-memoria-card"
                >
                  {vText && (
                    <p className="font-display text-navy/90 leading-relaxed italic text-lg">
                      &ldquo;{vText}&rdquo;
                    </p>
                  )}
                  {vRef && (
                    <p className="mt-2 text-[11px] uppercase tracking-widest text-gold">
                      {vRef}
                    </p>
                  )}
                </div>
              </div>
            );
          })()}

          <div className="mt-6 flex justify-end" data-testid="devocional-share-slot">
            <ShareButton
              variant="pill"
              label="Compartilhar Esperança"
              contentType="devocional"
              contentId={devo.id || "today"}
              title={devo.title}
              mainText={devo.impact_phrase}
              verse={devo.verse_text}
              verseRef={devo.verse_ref}
              application={devo.application}
              prayer={devo.prayer}
              reflection={devo.reflection}
              theme={devo.title}
            />
          </div>

          <JournalPrompt
            refKey={`devo-${devo.id || "today"}`}
            refLabel={`${devo.title || "Devocional"}, ${new Date().toLocaleDateString("pt-BR")}`}
            type="devotional"
            devotionalId={devo.id || null}
          />

          <div className="mt-8 space-y-4">
            <NewsletterCTA
              interest="daily"
              title="Receba este devocional por e-mail"
              subtitle="Versículo, devocional e oração todos os dias na sua caixa de entrada."
              testidPrefix="devotional-newsletter"
            />
            <PrayerWeekCard compact />
          </div>

          <div className="gold-rule my-12" />

          {all.length > 1 && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50 mb-4">
                Devocionais recentes
              </p>
              <div className="space-y-3">
                {all
                  .filter((d) => d.id !== devo.id)
                  .slice(0, 5)
                  .map((d) => (
                    <Link
                      key={d.id}
                      to={`/devocional/${d.id}`}
                      data-testid={`devotional-item-${d.id}`}
                      className="block p-4 border border-border rounded-xl hover:border-gold/60 bg-white transition"
                    >
                      <div className="text-xs text-gold">{d.verse_ref}</div>
                      <div className="font-display text-lg text-navy mt-1">{d.title}</div>
                    </Link>
                  ))}
              </div>
            </div>
          )}
        </article>
      ) : (
        <p className="text-navy/60">Nenhum devocional disponível no momento.</p>
      )}
    </div>
  );
}
